import { Location } from '@teko/location';
import { Address } from '@teko/user';
import { useServices } from 'contexts';
import { useEffect, useState } from 'react';

export interface LocationLimit {
  provinces?: string[];
  districts?: string[];
  wards?: string[];
}

export const useLocations = (
  defaultAddress: Partial<Address>,
  limit: LocationLimit = {},
) => {
  const { locationService } = useServices();
  const [provinces, setProvinces] = useState<Location[]>([]);
  const [districts, setDistricts] = useState<Location[]>([]);
  const [wards, setWards] = useState<Location[]>([]);
  const [province, setProvince] = useState<Location | null>(
    defaultAddress.provinceCode
      ? {
          code: defaultAddress.provinceCode,
          name: defaultAddress.provinceName || '',
        }
      : null,
  );
  const [district, setDistrict] = useState<Location | null>(
    defaultAddress.districtCode
      ? {
          code: defaultAddress.districtCode,
          name: defaultAddress.districtName || '',
        }
      : null,
  );
  const [ward, setWard] = useState<Location | null>(
    defaultAddress.wardCode
      ? {
          code: defaultAddress.wardCode || '',
          name: defaultAddress.wardName || '',
        }
      : null,
  );

  const {
    provinces: limitProvinces = [],
    districts: limitDistricts = [],
    wards: limitWards = [],
  } = limit;
  useEffect(() => {
    locationService.getProvinces().then((provs) => {
      setProvinces(filterLocations(provs, limitProvinces));
    });

    const { provinceCode, districtCode } = defaultAddress;

    if (provinceCode) {
      locationService
        .getDistrictsByProvinceCode(provinceCode)
        .then((allDistricts) => {
          setDistricts(filterLocations(allDistricts, limitDistricts));
        });
    }

    if (districtCode) {
      locationService.getWardsByDistrictCode(districtCode).then((allWards) => {
        setWards(filterLocations(allWards, limitWards));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // computed value to check address filling status
  const isValidLocation = () => {
    return province?.code && district?.code && (ward?.code || !wards.length);
  };
  const onProvinceChanged = async (code: Location['code']) => {
    const item = provinces.find((province) => province.code === code);
    if (item) {
      setProvince(item);
      setDistrict(null);
      setWard(null);
      // reset list district and ward = []
      const allDistricts = await locationService.getDistrictsByProvinceCode(
        item.code,
      );
      setDistricts(filterLocations(allDistricts, limitDistricts));
      setWards([]);
    } else {
      setProvince(null);
      setDistricts([]);
      setWards([]);
    }
  };
  const onDistrictChanged = async (code: Location['code']) => {
    const item = districts.find((district) => district.code === code);
    if (item) {
      setDistrict(item);
      setWard(null);

      // reset list ward;
      const allWards = await locationService.getWardsByDistrictCode(item.code);
      setWards(filterLocations(allWards, limitWards));
    } else {
      setDistrict(null);
      setWards([]);
    }
  };
  const onWardChanged = (code: Location['code']) => {
    const item = wards.find((ward) => ward.code === code);
    if (item) setWard(item);
  };

  const setLocation = ({
    provinceCode,
    districtCode,
    wardCode,
  }: {
    provinceCode: string;
    districtCode: string;
    wardCode: string;
  }) => {
    const province = provinces.find((p) => p.code === provinceCode);
    if (!province) return;
    (async function getDetailAndSetLocation() {
      const [districts = [], wards = []] = await Promise.all([
        locationService.getDistrictsByProvinceCode(provinceCode),
        locationService.getWardsByDistrictCode(districtCode),
      ] as Promise<Location[]>[]);
      const district = districts.find((d) => d.code === districtCode);
      const ward = wards.find((w) => w.code === wardCode);
      setDistricts(districts);
      if (!!district) {
        setDistrict(district);
      }
      setWards(wards);
      if (!!ward) {
        setWard(ward);
      }
      setProvince(province);
    })();
  };

  return {
    isValidLocation,
    wards,
    provinces,
    districts,
    ward,
    province,
    district,
    onProvinceChanged,
    onDistrictChanged,
    onWardChanged,
    setLocation,
  };
};

export const filterLocations = (locations: Location[], limits: string[]) => {
  return limits && limits.length > 0
    ? locations.filter((location) =>
        limits.some((allowDis) => {
          const re = new RegExp(`^${allowDis}$`);
          return re.test(location.code);
        }),
      )
    : locations;
};
